/**
 * As quatro chamadas do convidado, e a separação entre "o servidor recusou" e
 * "não falamos com o servidor".
 *
 * `servicos/api.ts` já distingue as duas coisas — recusa volta 200 com
 * `{ok:false}`, defeito vira `ErroDaApi` — mas devolve uma delas por `return` e
 * a outra por `throw`. Quem escreve tela não deveria ter que lembrar disso a
 * cada chamada: um `try` esquecido no meio do preenchimento é a tela branca que
 * o convidado fecha sem avisar ninguém.
 *
 * Aqui os três caminhos viram um `Resultado` só, e a decisão de o que fazer com
 * cada um fica visível em `switch`.
 *
 * Este arquivo NÃO conhece o Supabase: ele chama `api.ts`, que é o único que
 * conhece. `testes/disciplina.test.ts` quebra o build se isso mudar.
 */

import type {
  EnvelopeDoConvite,
  FimDoConvite,
  RecusaDoConvite,
  RetomadaDoConvite,
  Valor,
} from '../tipos';
import { abrirFormulario, concluir, responder, retomar } from './api';

/**
 * O que a tela escreve quando o servidor não respondeu. É texto nosso, e não do
 * banco, porque o banco não disse nada — é justamente o caso em que não há
 * `mensagem` para mostrar como está.
 *
 * As duas frases são diferentes de propósito: na abertura não há nada a
 * preservar, no meio do preenchimento há — e prometer que a resposta continua
 * ali é o que faz o convidado tocar de novo em vez de fechar.
 */
export const FALHA_AO_ABRIR =
  'Não conseguimos carregar o convite. Verifique sua conexão e toque em "Tentar de novo".';

export const FALHA_AO_ENVIAR =
  'Não conseguimos enviar sua resposta. Verifique sua conexão e toque em ' +
  '"Tentar de novo" — o que você respondeu continua aqui.';

export type Resultado<T> =
  | { readonly estado: 'ok'; readonly envelope: T }
  /** Regra de negócio. `recusa.mensagem` é mostrada COMO ESTÁ. */
  | { readonly estado: 'recusa'; readonly recusa: RecusaDoConvite }
  /** Rede, permissão, função ausente. A mensagem é nossa. */
  | { readonly estado: 'falha'; readonly mensagem: string };

/**
 * O envelope de sucesso e a recusa chegam pelo mesmo `return`, distinguidos por
 * `ok`. Não confie no formato: um proxy que devolva HTML de erro com 200 cai
 * aqui, e tratar isso como sucesso mostraria uma tela sem pergunta nenhuma.
 */
function separar<T extends { readonly ok: true }>(
  bruto: T | RecusaDoConvite,
  mensagemDeFalha: string,
): Resultado<T> {
  if (bruto && typeof bruto === 'object' && 'ok' in bruto) {
    if (bruto.ok === true) return { estado: 'ok', envelope: bruto as T };
    if (bruto.ok === false) return { estado: 'recusa', recusa: bruto };
  }
  return { estado: 'falha', mensagem: mensagemDeFalha };
}

/**
 * A interface que a tela consome. Existe para o teste renderizar o percurso
 * inteiro sem rede: os três caminhos de um RSVP são o comportamento central do
 * produto, e prová-los não pode depender de um Supabase de pé.
 */
export type ServicoDoConvite = {
  abrir(token: string): Promise<Resultado<EnvelopeDoConvite>>;
  responder(
    respostaToken: string,
    noId: string,
    valor: Valor,
  ): Promise<Resultado<EnvelopeDoConvite>>;
  concluir(respostaToken: string): Promise<Resultado<FimDoConvite>>;
  /**
   * Retoma uma resposta em andamento — a chamada do F5 (0010).
   *
   * A mensagem de falha é a de ENVIAR, e não a de abrir: quem chega aqui já
   * respondeu algo, e "o que você respondeu continua aqui" é verdade. Dizer
   * "não conseguimos carregar o convite" faria a pessoa achar que perdeu.
   */
  retomar(respostaToken: string): Promise<Resultado<RetomadaDoConvite>>;
};

export const servicoDoConvite: ServicoDoConvite = {
  async abrir(token) {
    try {
      return separar<EnvelopeDoConvite>(await abrirFormulario(token), FALHA_AO_ABRIR);
    } catch {
      return { estado: 'falha', mensagem: FALHA_AO_ABRIR };
    }
  },

  async responder(respostaToken, noId, valor) {
    try {
      return separar<EnvelopeDoConvite>(
        await responder(respostaToken, noId, valor),
        FALHA_AO_ENVIAR,
      );
    } catch {
      return { estado: 'falha', mensagem: FALHA_AO_ENVIAR };
    }
  },

  async concluir(respostaToken) {
    try {
      return separar<FimDoConvite>(await concluir(respostaToken), FALHA_AO_ENVIAR);
    } catch {
      return { estado: 'falha', mensagem: FALHA_AO_ENVIAR };
    }
  },

  async retomar(respostaToken) {
    try {
      return separar<RetomadaDoConvite>(await retomar(respostaToken), FALHA_AO_ENVIAR);
    } catch {
      return { estado: 'falha', mensagem: FALHA_AO_ENVIAR };
    }
  },
};
